// src/components/Dialogs/RecentTab.jsx

import React, { useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress
} from '@mui/material';
import FormCard from '../Cards/FormCard';

/**
 * RecentTab:
 *  1) Displays "recent" forms grouped by time ranges: Today, Yesterday, Last week, This month, Last month, Older.
 *  2) Shows "No forms found" if none are present and not loading.
 *  3) Implements infinite scrolling via an IntersectionObserver on a sentinel div.
 *  4) Highlights the selected form using `selectedDoc`.
 *
 * Props:
 *  - forms: array of form objects (already fetched by the parent).
 *    Each form is expected to have at least one of these fields:
 *      - form.lastModified (Firestore Timestamp or date string), or
 *      - form.publishedAt (Firestore Timestamp or date string).
 *  - isLoading: boolean, indicates if we are currently fetching more forms.
 *  - hasMore: boolean, indicates if there are more forms to load.
 *  - fetchNextBatch: function to load the next batch of forms from Firestore (for infinite scroll).
 *  - selectedDoc: the currently selected doc (object or ID) from the parent.
 *  - onSelectDoc: function(formObj) => sets the selected doc in the parent.
 */
export default function RecentTab({
  forms = [],
  isLoading,
  hasMore,
  fetchNextBatch,
  selectedDoc,
  onSelectDoc
}) {
  // We'll create a sentinel ref for the IntersectionObserver, to trigger "load more" logic
  const sentinelRef = useRef(null);

  /**
   * Intersection Observer logic:
   * Each time the sentinel is visible and we have more forms to load,
   * we call fetchNextBatch() if we're not already loading.
   */
  useEffect(() => {
    // If there's no sentinel or no more forms to load, skip setting up the observer
    if (!sentinelRef.current) return;
    if (!hasMore) return;

    // Create a new intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        // If the sentinel is visible in the viewport and we're not already loading => fetch next batch
        if (firstEntry.isIntersecting && !isLoading && fetchNextBatch) {
          fetchNextBatch();
        }
      },
      {
        root: null,         // observe the viewport
        rootMargin: '200px',// triggers earlier, ~200px before sentinel hits the viewport
        threshold: 0
      }
    );

    // Start observing the sentinel div
    observer.observe(sentinelRef.current);

    // Cleanup on unmount or re-render
    return () => {
      observer.disconnect();
    };
  }, [isLoading, hasMore, fetchNextBatch]);

  /**
   * Step 1) Group the forms by recency categories:
   *    - Today (0 days ago)
   *    - Yesterday (1 day ago)
   *    - Last week (2-6 days ago)
   *    - This month (7-29 days ago)
   *    - Last month (30-59 days ago)
   *    - Older (>= 60 days ago)
   *
   * We'll use a helper function to categorize each form based on either form.lastModified or form.publishedAt.
   */
  const groupedForms = useMemo(() => {
    // Current time reference
    const now = new Date();

    // Helper function to determine which category a form belongs to
    function getCategory(dateField) {
      // If there's no valid date at all, treat as "Older"
      if (!dateField) return 'Older';

      let formDate;

      // Case 1: Firestore Timestamp object => call .toDate()
      if (typeof dateField === 'object' && typeof dateField.toDate === 'function') {
        formDate = dateField.toDate();
      }
      // Case 2: Attempt to parse it as a string or standard JS Date
      else {
        formDate = new Date(dateField);

        // If parsing fails => treat as "Older"
        if (isNaN(formDate.getTime())) {
          return 'Older';
        }
      }

      // Compute the difference in days from now
      const diffTime = now - formDate; // difference in milliseconds
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays >= 2 && diffDays < 7) {
        return 'Last week';
      } else if (diffDays >= 7 && diffDays < 30) {
        return 'This month';
      } else if (diffDays >= 30 && diffDays < 60) {
        return 'Last month';
      } else {
        return 'Older';
      }
    }

    // Initialize all categories as empty arrays
    const categories = {
      Today: [],
      Yesterday: [],
      'Last week': [],
      'This month': [],
      'Last month': [],
      Older: []
    };

    // Distribute each form into one of the 6 categories
    for (const form of forms) {
      // We'll check lastModified first, then fallback to publishedAt if lastModified is missing
      const dateField = form.lastModified || form.publishedAt;
      const category = getCategory(dateField);
      categories[category].push(form);
    }

    return categories;
  }, [forms]);

  // We want to display the categories in a specific order
  const categoryLabels = [
    'Today',
    'Yesterday',
    'Last week',
    'This month',
    'Last month',
    'Older'
  ];

  return (
    <>
      {/* If we have no forms at all and we're not currently loading, show "No forms found." */}
      {forms.length === 0 && !isLoading && (
        <Typography variant="body2" color="text.secondary">
          No forms found.
        </Typography>
      )}

      {/**
       * Step 2) Render each category if it has at least one form.
       *         We'll create a subheader (e.g. "Today") and then a horizontal scroll row of FormCards.
       */}
      {categoryLabels.map((label) => {
        const formsInCategory = groupedForms[label];
        if (!formsInCategory || formsInCategory.length === 0) {
          return null; // Don't render empty categories
        }

        return (
          <Box key={label} sx={{ mb: 0 /* spacing below each category section */ }}>
            {/* Subheader for the category:
                Use body2, medium weight, text.secondary color to match Google Sheets style */}
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 'medium',
                color: 'text.secondary'
              }}
            >
              {label}
            </Typography>

            {/**
             * We use a Grid container with wrap="nowrap" and overflowX="auto"
             * so the forms appear in a single horizontal row.
             */}
            <Grid
              container
              spacing={4}
              wrap="nowrap"
              sx={{ overflowX: 'auto' }}
            >
              {formsInCategory.map((formObj) => {
                // docId is used as a React key
                const docId = formObj.id || formObj;
                // docName for display if no form.title
                const docName = formObj.title || formObj;
                // Check if this card is selected
                const isSelected = selectedDoc?.id === docId || selectedDoc === docName;

                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    key={docId}
                    sx={{ flex: '0 0 auto' }}
                  >
                    <FormCard
                      form={formObj}
                      onClick={() => onSelectDoc?.(formObj)}
                      isSelected={isSelected}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );
      })}

      {/* If we are loading more data, show a spinner */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/**
       * The sentinel div for infinite scrolling.
       * Our IntersectionObserver monitors this ref => triggers fetchNextBatch() once in view.
       */}
      <Box
        ref={sentinelRef}
        sx={{ height: '1px', width: '100%', mt: 2 }}
      />
    </>
  );
}
